<template>
    <div class="variant-picker-wrapper">
        <div class="d-flex justify-content-end">
            <b-pagination
                v-model="currentPage"
                size="md"
                :per-page="productPerPage"
                :total-rows="totalCount"
                @change="onFilterChange"
            />
            <b-form-input
                v-model="searchVal"
                class="mb-3 ml-3"
                placeholder="Search"
                debounce="400"
                @update="onFilterChange(1)"
            />
        </div>
        <select-table :fields="tableFields" :items="filteredItems" @item-selected="onVariantSelected">
            <template #cell(actions)="row">
                <button class="btn btn-turquise" @click="row.toggleDetails">Show Details</button>
            </template>
            <template #row-details="{ item }">
                <b-card no-body class="py-3">
                    <div class="row mx-0">
                        <ul class="col-6 m-0 pl-5">
                            <li v-for="(option, idx) of item.options" :key="`${option}-${idx}`">
                                <span class="font-weight-bold text-capitalize mr-1">
                                    {{ formattedOptionName(option.name) }}:
                                </span>
                                {{ option.value }}
                            </li>
                        </ul>
                        <div v-if="item.image" class="col-6 text-right pr-5">
                            <b-img rounded thumbnail height="120" width="120" :src="item.image" />
                        </div>
                    </div>
                </b-card>
            </template>
        </select-table>
        <div class="d-flex justify-content-between">
            <button class="btn btn-secondary" @click="cancel">Back</button>
            <button class="btn btn-primary" :disabled="!selectedVariant" @click="variantSelected">Select</button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { Ref, computed, onBeforeMount, ref } from 'vue';
import { GenericPodProduct, GenericPodVariant } from '@/api/typings/podProviders';
import SelectTable from '@/components/SelectTable/SelectTable.vue';

const emit = defineEmits<{
    (event: 'variant-selected', value: GenericPodVariant): void;
    (event: 'cancel'): void;
}>();

const props = defineProps<{
    selectedProduct: GenericPodProduct;
}>();

const tableFields = [{ key: 'title', label: 'Variant' }, 'actions'];
const productPerPage = 10;
const currentPage = ref(1);
const totalCount = ref(10);
const searchVal = ref('');
const filteredItems: Ref<GenericPodVariant[]> = ref([]);
const selectedVariant: Ref<GenericPodVariant | null> = ref(null);

const formattedOptionName = computed(() => {
    return (value: string) => {
        const title = value.replace(/([A-Z])/g, ' $1');
        return `${title.charAt(0).toUpperCase()}${title.slice(1)}`;
    };
});

onBeforeMount(() => {
    onFilterChange(1);
});

const onFilterChange = (pageNumber: number) => {
    let variants = JSON.parse(JSON.stringify(props.selectedProduct.variants)) as GenericPodVariant[];
    currentPage.value = pageNumber;
    const startIndex = (pageNumber - 1) * productPerPage;
    const endIndex = startIndex + productPerPage;

    if (searchVal.value?.length) {
        variants = variants.filter((v) => v.title.toLowerCase().includes(searchVal.value.toLowerCase()));
    }

    totalCount.value = variants.length;
    filteredItems.value = variants.slice(startIndex, endIndex);
};

const onVariantSelected = (value: unknown) => {
    const [variant] = value as GenericPodVariant[];
    selectedVariant.value = variant;
};

const variantSelected = () => {
    emit('variant-selected', selectedVariant.value!);
};

const cancel = () => {
    emit('cancel');
};
</script>
